/**
 * `useSse()` subscribes to /api/events and invalidates queries by job
 * (contract §2.9). Live toggle off closes the stream. If the stream drops
 * (no heartbeat within 45 s, or EventSource error) status becomes "stale",
 * the hook reconnects with backoff and polls /api/health every 10 s meanwhile.
 *
 * It also carries the `alert` frame: a rule firing, pushed into the live
 * feed and raised as an OS notification when this tab has permission. That
 * is a convenience for a tab that is already open — the durable path is
 * browser push (`api/push.ts`), which reaches a browser with no tab at
 * all, and both end up in the same log.
 */
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { create } from "zustand";
import { keysForSseJob } from "./keys";
import { fetchJson, paths } from "./client";
import { notifyLocally } from "./push";
import { useAlertFeed } from "@/state/alerts";
import type { Health, SseAlert, SseChange, SseHello } from "./types";

export type SseStatus = "off" | "connecting" | "open" | "stale";

interface SseState {
  status: SseStatus;
  lastEventAt: number | null;
  lastChange: SseChange | null;
  /** db_version per job from hello/change events */
  versions: Record<string, number>;
  lastBlocks: Record<string, number | undefined>;
  changes: number;
  set: (patch: Partial<SseState>) => void;
}

export const useSseStore = create<SseState>((set) => ({
  status: "off",
  lastEventAt: null,
  lastChange: null,
  versions: {},
  lastBlocks: {},
  changes: 0,
  set: (patch) => set(patch),
}));

const HEARTBEAT_TIMEOUT_MS = 45_000;
const POLL_MS = 10_000;

/** Where an alert's notification should land (mirrors push.rs::target). */
function alertTarget(alert: SseAlert["alert"]): string {
  if (alert.token && alert.chain) {
    return alert.subject
      ? `/t/${alert.chain}/${alert.token}/family/${alert.subject}`
      : `/t/${alert.chain}/${alert.token}`;
  }
  return "/alerts";
}

export function useSse(enabled: boolean): SseStatus {
  const qc = useQueryClient();
  const status = useSseStore((s) => s.status);

  useEffect(() => {
    const store = useSseStore.getState();
    if (!enabled || typeof EventSource === "undefined") {
      store.set({ status: "off" });
      return;
    }
    let es: EventSource | null = null;
    let watchdog: ReturnType<typeof setTimeout> | null = null;
    let reconnect: ReturnType<typeof setTimeout> | null = null;
    let poll: ReturnType<typeof setInterval> | null = null;
    let backoff = 1000;
    let disposed = false;

    const invalidate = (job: string) => {
      for (const key of keysForSseJob(job)) void qc.invalidateQueries({ queryKey: key });
    };

    const touch = () => {
      useSseStore.getState().set({ lastEventAt: Date.now(), status: "open" });
      if (watchdog) clearTimeout(watchdog);
      watchdog = setTimeout(() => {
        useSseStore.getState().set({ status: "stale" });
        restart();
      }, HEARTBEAT_TIMEOUT_MS);
    };

    const stopPolling = () => {
      if (poll) clearInterval(poll);
      poll = null;
    };

    // The fallback poll compares /api/health's `last_block`, which lives in
    // its own map: writing it into `versions` (db_version, from hello and
    // change) would make the first poll after any drop look like a change
    // for every job, and the next `hello` look like one again.
    const startPolling = () => {
      if (poll) return;
      poll = setInterval(() => {
        fetchJson<Health>(paths.health())
          .then(({ data }) => {
            const s = useSseStore.getState();
            const next: Record<string, number | undefined> = { ...s.lastBlocks };
            let changed = false;
            for (const j of data.jobs) {
              const seen = next[j.job];
              if (seen !== undefined && seen !== j.last_block) invalidate(j.job);
              if (seen !== j.last_block) {
                next[j.job] = j.last_block;
                changed = true;
              }
            }
            if (changed) s.set({ lastBlocks: next, changes: s.changes + 1 });
          })
          .catch(() => undefined);
      }, POLL_MS);
    };

    const restart = () => {
      if (disposed) return;
      es?.close();
      es = null;
      startPolling();
      if (reconnect) clearTimeout(reconnect);
      reconnect = setTimeout(connect, backoff);
      backoff = Math.min(backoff * 2, 30_000);
    };

    const connect = () => {
      if (disposed) return;
      useSseStore.getState().set({ status: "connecting" });
      es = new EventSource(paths.events(), { withCredentials: true });
      es.addEventListener("open", () => {
        backoff = 1000;
        stopPolling();
        touch();
      });
      es.addEventListener("hello", (ev) => {
        touch();
        try {
          const hello = JSON.parse((ev as MessageEvent<string>).data) as SseHello;
          const s = useSseStore.getState();
          const versions: Record<string, number> = { ...s.versions };
          const lastBlocks = { ...s.lastBlocks };
          for (const j of hello.jobs) {
            if (versions[j.job] !== undefined && versions[j.job] !== j.db_version) invalidate(j.job);
            versions[j.job] = j.db_version;
            lastBlocks[j.job] = j.last_block;
          }
          s.set({ versions, lastBlocks });
        } catch {
          /* ignore malformed hello */
        }
      });
      es.addEventListener("change", (ev) => {
        touch();
        try {
          const change = JSON.parse((ev as MessageEvent<string>).data) as SseChange;
          const s = useSseStore.getState();
          s.set({
            lastChange: change,
            changes: s.changes + 1,
            versions: { ...s.versions, [change.job]: change.db_version },
            lastBlocks: { ...s.lastBlocks, [change.job]: change.last_block },
          });
          invalidate(change.job);
        } catch {
          /* ignore */
        }
      });
      // A firing: show it now, and refetch the log the `change` that
      // accompanies it would have refetched anyway.
      es.addEventListener("alert", (ev) => {
        touch();
        try {
          const frame = JSON.parse((ev as MessageEvent<string>).data) as SseAlert;
          const known = useAlertFeed.getState().toasts.some((t) => t.id === frame.alert.id);
          useAlertFeed.getState().arrived(frame.alert, frame.unread);
          // Only when the rule asked for the browser, and only once: a
          // reconnect that replays a frame must not buzz twice.
          if (frame.alert.delivered && frame.channels?.browser !== false && !known) {
            void notifyLocally(frame.alert, alertTarget(frame.alert));
          }
          invalidate("frontend");
        } catch {
          /* ignore malformed alert */
        }
      });
      es.addEventListener("heartbeat", touch);
      es.addEventListener("error", () => {
        useSseStore.getState().set({ status: "stale" });
        restart();
      });
    };

    connect();
    return () => {
      disposed = true;
      es?.close();
      if (watchdog) clearTimeout(watchdog);
      if (reconnect) clearTimeout(reconnect);
      stopPolling();
      useSseStore.getState().set({ status: "off" });
    };
  }, [enabled, qc]);

  return status;
}
